import { ReactNode, useEffect, useMemo } from 'react'
import PostalCodeStep from './steps/PostalCodeStep'
import EnergyTypeStep from './steps/EnergyTypeStep'
import { SimulationSteps } from './types'
import UsageStep from './steps/UsageStep'
import PropositionStep from './steps/PropositionStep'
import SimulationSideCard from 'features/simulation/components/simulation-side-card/SimulationSideCard'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/customer/enums'
import SimulationLayout from './layouts/simulation-layout/SimulationLayout'
import { useBeforeunload } from 'react-beforeunload'
import { updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { SIMULATION_DOWN } from 'constants/envs'
import LeaveInfoDuringDowntimeStep from 'features/simulation/steps/LeaveInfoDuringDowntimeStep'
import styles from 'features/simulation/layouts/SimulationLayouts.module.scss'
import { SOURCE_TYPES } from '@boltenergy-be/shared'
import { SCHEDULED_DOWNTIME } from 'constants/constants'
import dayjs from 'dayjs'
import ReviewRatings from 'components/review-ratings/ReviewRatings'

const Simulation = () => {
  // Redux store
  const { currentStep } = useStoreSelector((store) => store.customer.flows.simulation)
  const { selectedProposition } = useStoreSelector((store) => store.customer.propositions)
  const { sourceType } = useStoreSelector((store) => store.marketing)
  const dispatch = useStoreDispatch()

  // Shows dialog when user tries to reload/leave, but not once they've committed to a proposition
  useBeforeunload((e) => {
    if (!selectedProposition) {
      e.preventDefault()
    }
  })

  // Constants
  const activeScheduledDowntime = SCHEDULED_DOWNTIME.find(({ start, end }) => dayjs().isBetween(start, end))
  const showLeaveSimulationDuringDowntime = activeScheduledDowntime || (SIMULATION_DOWN && sourceType !== SOURCE_TYPES.push)
  const showProducerCard =
    !showLeaveSimulationDuringDowntime && (sourceType === SOURCE_TYPES.pull || currentStep === SimulationSteps.PROPOSITION)

  // Memo
  const currentSimulationStep = useMemo<ReactNode>(() => {
    switch (currentStep) {
      case SimulationSteps.POSTAL_CODE:
        return <PostalCodeStep />
      case SimulationSteps.ENERGY_TYPE:
        return <EnergyTypeStep />
      case SimulationSteps.USAGE:
        return <UsageStep />
      case SimulationSteps.PROPOSITION:
        return <PropositionStep />
      default:
        return null
    }
  }, [currentStep])

  /**
   * Sets the email, firstname and lastname if they are specified in the URL
   * Only triggered on first render
   */
  useEffect(() => {
    // Set email, firstname & lastname in the store
    const search = new URLSearchParams(window.location.search)
    if (search.has('email')) {
      dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.EMAIL, value: search.get('email') }))
    }
    if (search.has('firstname')) {
      dispatch(
        updateInput({
          group: InputGroupKeys.PERSONAL_DATA,
          key: PersonalDataFieldKeys.FIRSTNAME,
          value: search.get('firstname')
        })
      )
    }
    if (search.has('lastname')) {
      dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.LASTNAME, value: search.get('lastname') }))
    }
  }, [])

  return (
    <SimulationLayout fullWidth={!showProducerCard}>
      <section className={styles['current-step']}>
        {showLeaveSimulationDuringDowntime ? <LeaveInfoDuringDowntimeStep /> : currentSimulationStep}

        <footer>
          <ReviewRatings className="pt-500" />
        </footer>
      </section>

      {/* PRODUCER - show always for PULL or only on proposition step for PUSH  */}
      {showProducerCard && <SimulationSideCard />}
    </SimulationLayout>
  )
}

export default Simulation
